import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  Button,
  Spacer,
  Stack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { Accident } from '../../../../../../../types/ProductFactory';
import { object } from 'yup';
import {
  dateMaxFiveYearsAgo,
  string,
} from '../../../../../../common/src/constants/validationSchemas';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { format } from 'date-fns';
import AccidentEditModalBody from './AccidentEditModalBody';

interface Props {
  accident?: Accident;
  onSave: (arg0: Accident) => void;
  onCancel: () => void;
  isOpen: boolean;
  onRemove?: () => void;
}

type Inputs = {
  date: string;
  type?: string;
  liability: string;
};

const AccidentEditModal: React.FC<Props> = ({
  onSave,
  accident,
  isOpen,
  onCancel,
  onRemove,
}) => {
  const { t } = useTranslation();

  const schema = object().shape({
    date: dateMaxFiveYearsAgo,
    liability: string.required,
    type: string.notRequired,
  });

  const methods = useForm<Inputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...(accident?.date && {
        date: accident?.date || '',
      }),
      type: accident?.type || '',
      liability: accident?.liability || '',
    },
  });

  const onSubmit = ({ date, type, liability }: Inputs) => {
    onSave({
      date: format(new Date(date), `yyyy-MM-dd'T'HH:mm:ss.SSSxxx`),
      type,
      liability,
    });
    methods.reset();
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('Edit Accident')}</ModalHeader>
        <ModalCloseButton />
        <FormProvider {...methods}>
          <AccidentEditModalBody />
        </FormProvider>
        <ModalFooter
          as={Stack}
          direction={{ base: 'column', mobileL: 'row' }}
          alignItems={{ base: 'stretch', mobileL: 'unset' }}
        >
          {onRemove && (
            <Button
              variant={{ base: 'dangerSecondary', mobileL: 'dangerGhost' }}
              onClick={onRemove}
            >
              {t('Remove')}
            </Button>
          )}
          <Spacer />
          <Button
            variant={{ base: 'secondary', mobileL: 'ghost' }}
            onClick={onCancel}
          >
            {t('Cancel')}
          </Button>
          <Button onClick={methods.handleSubmit(onSubmit)}>{t('Save')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AccidentEditModal;
